import type { FC } from "react";
import type { TagGroup } from "../../../../merge-tag/components/types";
import { useDataTable } from "../../utils/use-data-table";
import { AddButton } from "../toolbar/add-button";
import { AddDivider } from "../toolbar/add-divider";
import { AddImagesAndFilesButton } from "../toolbar/add-images-and-files-button";
import { ColumnsButton } from "../toolbar/columns-button";
import { DesignHuddleButton } from "../toolbar/design-huddle-button";
import { DynamicContentButton } from "../toolbar/dynamic-content-button/dynamic-content-button";
import { FromContentLibraryButton } from "../toolbar/from-content-library-button";
import type { LexicalToolbarState } from "../toolbar/hooks/use-lexical-toolbar";
import { ImageButton } from "../toolbar/image-button";
import { LocalImageButton } from "../toolbar/local-image-button";
import { TableButton } from "../toolbar/table-button";
import type { GenericToolbarProps } from "./generic-toolbar";
import type { GenericToolbarDropdownElement } from "./types";

export const ToolbarDropdownElement: FC<
  {
    element: GenericToolbarDropdownElement;
    toolbarState: LexicalToolbarState;
    onClose: () => void;
    mode: "toolbar" | "floating";
    filterTags: (x: string) => TagGroup;
  } & Omit<
    GenericToolbarProps,
    "channel" | "topLevelElements" | "openModal" | "rightAlignedElements"
  >
> = ({ element, disabled, filterTags, toolbarState, onClose, mode }) => {
  const lexical_table_enabled = useDataTable();

  switch (element.type) {
    case "localImageButton":
      return (
        <LocalImageButton
          disabled={disabled}
          accept="image/apng, image/avif, image/gif, image/ico, image/jpg, image/jpeg, image/png, image/svg, image/webp"
          label="Upload image"
          onClick={onClose}
          mode={mode}
        />
      );

    case "imageButton":
      return (
        <ImageButton disabled={disabled} label="Image from URL" onClose={onClose} mode={mode} />
      );

    case "designHuddleButton":
      return (
        <DesignHuddleButton
          disabled={disabled}
          label="Design an image"
          onClick={onClose}
          mode={mode}
        />
      );

    case "addButton":
      return (
        <AddButton
          disabled={disabled}
          filterTags={filterTags}
          label="Button"
          onClick={onClose}
          channel={element.channel}
          mode={mode}
        />
      );

    case "addDivider":
      return (
        <AddDivider
          disabled={disabled}
          channel={element.channel}
          label="Divider"
          onClick={onClose}
          mode={mode}
        />
      );

    case "columnsButton":
      return (
        <ColumnsButton
          disabled={toolbarState.tableNodeSelected || disabled}
          label="Columns"
          onClick={onClose}
          mode={mode}
        />
      );

    case "tableButton":
      return (
        lexical_table_enabled && (
          <TableButton
            disabled={toolbarState.tableNodeSelected || disabled}
            label="Table"
            onClick={onClose}
            mode={mode}
          />
        )
      );

    case "fromContentLibrary":
      return (
        <FromContentLibraryButton
          disabled={disabled}
          label="From Content Library"
          onClose={onClose}
          mode={mode}
        />
      );

    case "addImagesAndFiles":
      return (
        <AddImagesAndFilesButton
          disabled={disabled}
          label="From Content Library"
          onClose={onClose}
          mode={mode}
        />
      );

    case "dynamicContent":
      return (
        <DynamicContentButton
          disabled={disabled}
          label="Dynamic Content"
          mode={mode}
          onClose={onClose}
        />
      );

    default:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      throw new Error(`Unsupported ToolbarDropdownElement type: ${(element as any).type}`);
  }
};
